/**
 * RowActions — Inline action menu for data grid rows.
 *
 * Props:
 *  - actions: Array<{ label, icon, onClick, color, divider, disabled }>
 *  - row: the current row data (passed to onClick handlers)
 */
import React, { useState, useCallback, memo } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Tooltip } from '@mui/material';
import { MoreVert as MoreIcon } from '@mui/icons-material';

function RowActions({ actions = [], row }) {
  const [anchor, setAnchor] = useState(null);

  const handleOpen = useCallback((e) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchor(null), []);

  const handleAction = useCallback((action) => {
    handleClose();
    if (action.onClick) action.onClick(row);
  }, [row, handleClose]);

  if (actions.length === 0) return null;

  return (
    <>
      <Tooltip title="Actions">
        <IconButton size="small" onClick={handleOpen} aria-label="Row actions" aria-haspopup="true">
          <MoreIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 160, mt: 0.5 } }}
      >
        {actions.map((action, i) => (
          action.divider ? <Divider key={`div-${i}`} /> : (
            <MenuItem
              key={action.label}
              onClick={() => handleAction(action)}
              disabled={action.disabled}
              sx={{ fontSize: '0.8125rem', color: action.color }}
            >
              {action.icon && <ListItemIcon sx={{ color: action.color }}>{<action.icon fontSize="small" />}</ListItemIcon>}
              <ListItemText primaryTypographyProps={{ variant: 'body2', color: action.color }}>{action.label}</ListItemText>
            </MenuItem>
          )
        ))}
      </Menu>
    </>
  );
}

export default memo(RowActions);
